import VectorLayer from 'ol/layer/Vector'
import ImageLayer from 'ol/layer/Image'
import TileLayer from 'ol/layer/Tile'
import LayerGroup from 'ol/layer/Group'
import { fromLonLat } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import ImageWMS from 'ol/source/ImageWMS'
import TileWMS from 'ol/source/TileWMS'
import OSM from 'ol/source/OSM'
import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Text from 'ol/style/Text'
import CircleStyle from 'ol/style/Circle'
import Icon from 'ol/style/Icon'
import {Feature} from 'ol';
import Point from 'ol/geom/Point';
import GeoJSON from 'ol/format/GeoJSON'
import { getDefaultMapCenter } from './map-defaults.js'
import { geoJsonReadOptions } from './projections.js'
import { geoServerWmsUrl } from './geoserver.js'
import VectorImageLayer from 'ol/layer/VectorImage'
import { bbox as bboxStrategy } from 'ol/loadingstrategy'
import SldStyleParser from 'geostyler-sld-parser'
import OlStyleParser from 'geostyler-openlayers-parser'
import {markRaw} from "vue";
import {Circle} from "ol/style";
import {getCenter} from "ol/extent";
import Photo from "ol-ext/style/Photo";

const sldParser = new SldStyleParser()
const olParser = new OlStyleParser()

const proxyGeoServerUrl = (url) => `/api/maps/geoserver-proxy?url=${encodeURIComponent(url)}`
const layerStateListenerRegistry = new WeakMap()

export const registerLayerStateListeners = (layers = [], onLayerStateChange = null) => {
    if (typeof onLayerStateChange !== 'function') return

    layers.forEach((layer) => {
        if (!layer) return

        let registeredCallbacks = layerStateListenerRegistry.get(layer)
        if (!registeredCallbacks) {
            registeredCallbacks = new Set()
            layerStateListenerRegistry.set(layer, registeredCallbacks)
        }

        if (registeredCallbacks.has(onLayerStateChange)) return

        registeredCallbacks.add(onLayerStateChange)
        layer.on('change:isDirty', onLayerStateChange)
        layer.on('change:name', onLayerStateChange)
    })
}

export const registerLayerInCurrentModule = (
    layer,
    {
        moduleSelected,
        modules,
        moduleViewOrders,
        getDesiredLayers,
        syncMapLayers,
        emitLayerData,
        getLayerOrderKey,
    } = {},
    { prepend = true } = {}
) => {
    if (!layer || !modules?.value || !moduleViewOrders?.value) return
    if (typeof getDesiredLayers !== 'function' || typeof syncMapLayers !== 'function') return
    if (typeof emitLayerData !== 'function' || typeof getLayerOrderKey !== 'function') return

    const selectedModule = moduleSelected?.value || 'Padrão'
    const currentModuleLayers = (modules.value[selectedModule] || []).filter(existingLayer => existingLayer !== layer)
    const nextModuleLayers = prepend ? [layer, ...currentModuleLayers] : [...currentModuleLayers, layer]

    layer.set('displayInLayerSwitcher', true)
    layer.set('layerSwitcherGroup', selectedModule)

    const currentDisplayedLayers = getDesiredLayers(selectedModule).filter(displayedLayer => displayedLayer !== layer)
    const nextDisplayedLayers = prepend ? [layer, ...currentDisplayedLayers] : [...currentDisplayedLayers, layer]
    const reorderedSelectedLayers = nextDisplayedLayers.filter(displayedLayer => nextModuleLayers.includes(displayedLayer))

    modules.value = { ...modules.value, [selectedModule]: reorderedSelectedLayers }
    moduleViewOrders.value = { ...moduleViewOrders.value, [selectedModule]: nextDisplayedLayers.map(getLayerOrderKey) }

    syncMapLayers(nextDisplayedLayers)
    emitLayerData()
}

export const createDefaultBaseStyle = (stroke, fill) => {
    return function(feature) {
        if (feature.get('useIcon')) {
            return null;
        }

        const featureFill = feature.get('color') || fill;
        const featureStroke = feature.get('strokeColor') || stroke;

        return new Style({
            fill: new Fill({ color: featureFill }),
            stroke: new Stroke({ color: featureStroke, width: 2 }),
            image: new Circle({ radius: 10, fill: new Fill({ color: featureFill }), stroke: new Stroke({ color: featureStroke, width: 2 }) })
        });
    };
};

export const generateLayerStyle = (baseStyle, labelField, labelColor = '#000000', labelSize = 12, labelFont = 'Arial') => {
    return function(feature, resolution) {
        if (feature.get('isText')) {
            const size = feature.get('fontSize') || '16';
            const font = feature.get('fontFamily') || 'Arial';
            const tColor = feature.get('textColor') || '#000000';

            return [new Style({
                text: new Text({
                    text: feature.get('texto'),
                    font: `bold ${size}px ${font}`,
                    fill: new Fill({ color: tColor }),
                    stroke: new Stroke({ color: '#fff', width: 3 }),
                    offsetY: -5
                })
            })];
        }

        const resolvedBaseStyle = typeof baseStyle === 'function'
            ? baseStyle(feature, resolution)
            : baseStyle;

        const baseStyles = normalizeStyles(resolvedBaseStyle)
            .map(stripTextFromStyle)
            .filter(Boolean);
        const iconStyle = createFeatureIconStyle(feature, resolution);

        const labelText = createLabelText(feature, labelField, labelColor, labelSize, labelFont);
        if (!labelText && !iconStyle) return baseStyles;

        const styles = [...baseStyles];

        if (iconStyle) {
            styles.push(iconStyle);
        }

        if (labelText) {
            styles.push(new Style({ text: labelText }));
        }

        return styles;
    };
};

export const addLayerToMap = (data, name, stroke, fill, labelField, tableInfo = null) => {
    const isMain = name === 'Lotes' || name === 'Edificações';

    let features = [];
    if (Array.isArray(data) && data.length > 0 && data[0] instanceof Feature) {
        features = data;
    } else if (data) {
        const validGeometryTypes = ['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon', 'GeometryCollection'];

        const cleanData = {
            ...data,
            features: data.features.filter((feature) => (
                feature?.type === 'Feature' &&
                feature?.geometry &&
                validGeometryTypes.includes(feature.geometry.type)
            ))
        };

        features = new GeoJSON().readFeatures(cleanData, geoJsonReadOptions());

        features.forEach((feature, index) => {
            if (feature.getId() === null) feature.setId(`${name}-${index}`);
        });
    }

    const source = new VectorSource({ features: features });

    const layer = markRaw(new VectorLayer({
        source: source,
        style: generateLayerStyle(createDefaultBaseStyle(stroke, fill), labelField),
        visible: isMain || !isMain
    }));
    layer.set('name', name);
    layer.set('isMain', isMain);
    layer.set('isDirty', false);
    layer.set('strokeColor', stroke);
    layer.set('fillColor', fill);
    layer.set('labelField', labelField);
    layer.set('labelColor', '#000000');
    layer.set('labelSize', 12);
    layer.set('labelFont', 'Arial');

    if (!!tableInfo) {
        layer.set('font', tableInfo.font);
        layer.set('fixedFilters', tableInfo.fixedFilters);
    }

    return layer;
};

export function addLayerToMapAndSwitcher(map, layer, options = {}) {
    if (!map || !layer) return false

    const {
        moduleName = null,
        position = 'top',
        displayInLayerSwitcher = true,
    } = options

    if (layer?.set) {
        layer.set('displayInLayerSwitcher', displayInLayerSwitcher)
        if (moduleName) layer.set('layerSwitcherGroup', moduleName)
    }

    const collection = map.getLayers?.()
    if (collection && !collection.getArray().includes(layer)) {
        map.addLayer(layer)
    }

    map.dispatchEvent?.({
        type: 'map-layer-switcher:add-layer',
        layer,
        moduleName,
        position,
    })

    return true
}

export function removeLayerFromMapAndSwitcher(map, layer) {
    if (!map || !layer) return false

    const collection = map.getLayers?.()
    if (collection?.getArray().includes(layer)) {
        map.removeLayer(layer)
    }

    map.dispatchEvent?.({
        type: 'map-layer-switcher:remove-layer',
        layer,
    })

    return true
}

export const applyGeoServerStyle = async (layer, gsLayerName) => {
    try {
        const workspace = gsLayerName.includes(':') ? gsLayerName.split(':')[0] : ''
        const baseUrl = window.geoServerUrl || 'http://172.19.192.9:8080'

        const wmsPath = workspace ? `geoserver/${workspace}/wms` : 'geoserver/wms'
        const wmsUrl = `${baseUrl}/${wmsPath}?request=GetStyles&layers=${gsLayerName}&service=wms&version=1.1.1`

        const proxyUrl = proxyGeoServerUrl(wmsUrl)

        const response = await window.axios.get(proxyUrl, { responseType: 'text' })
        const sldXml = response.data

        const { output: geoStylerStyle } = await sldParser.readStyle(sldXml)

        if (geoStylerStyle) {
            const { output: olStyle } = await olParser.writeStyle(geoStylerStyle)
            if (olStyle) layer.setStyle(olStyle)
        }
    } catch (error) {
        console.warn(`Aviso: Falha ao baixar/aplicar estilo dinâmico para a camada ${gsLayerName}.`, error)
    }
}

/**
 * Botão de tela cheia customizado (emitindo evento customizado)
 */
export function createCustomFullScreenControl() {
    const btn = document.createElement('button')
    btn.className = 'ol-unselectable ol-control'
    btn.title = 'Tela Cheia'
    btn.innerHTML = `<i class="fa fa-expand" aria-hidden="true"></i>`

    const container = document.createElement('div')
    container.className = 'ol-control ol-unselectable'
    container.style.position = 'absolute'
    container.style.top = '0.5em'
    container.style.left = '50%'
    container.style.transform = 'translateX(-50%)'
    container.appendChild(btn)

    btn.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('map-fullscreen', {
            detail: { page: 'home' }
        }))
    })

    return container
}

/**
 * Faz busca paginada de features de uma camada.
 * @param {string} layerName - Nome da tabela/camada (ex: geo_lote)
 * @param {{ date?: string, auditChangesOnly?: boolean, dateDiffOnly?: boolean, publicRequest?: boolean }} [options] - Se date (YYYY-MM-DD) for passado e a camada tiver _audit,
 *   retorna o estado naquela data. Com auditChangesOnly: true, retorna só alterações ocorridas naquele dia (tabela _audit).
 *   Com dateDiffOnly: true, retorna só feições cuja geometria na data difere do estado atual.
 */
export async function fetchAllFeatures(layerName, options = {}) {
    const params = new URLSearchParams()
    if (options.date) params.set('date', options.date)
    if (options.auditChangesOnly) params.set('audit_changes', '1')
    if (options.dateDiffOnly) params.set('date_diff', '1')
    const qs = params.toString()
    const base = options.publicRequest ? '/api/maps/public/layers' : '/api/maps/layers'
    const url = `${base}/${encodeURIComponent(layerName)}/geometry${qs ? '?' + qs : ''}`

    try {
        let response = await window.axios.get(url)
        const geojson = response.data.data

        if (!Array.isArray(geojson.features)) {
            console.warn(`Resposta inválida: 'features' ausente ou não é um array.`)
        }

        return {
            type: 'FeatureCollection',
            features: geojson.features || []
        }
    } catch (error) {
        console.warn(`Erro ao buscar dados da camada "${layerName}":`, error)
        return { type: 'FeatureCollection', features: [] }
    }
}

/**
 * Visualização de uma única camada (LayerViewer / PublicLayerViewer): força visível e listagem
 * no layer switcher, independentemente de active/display_in_layer_switcher no cadastro.
 * Aplica recursivamente a filhos de LayerGroup.
 */
export function applyViewerLayerSwitcherDefaults (layerData) {
    if (!layerData || typeof layerData !== 'object') return
    layerData.active = true
    layerData.display_in_layer_switcher = true
    const nested = layerData.layers
    if (Array.isArray(nested)) {
        nested.forEach(applyViewerLayerSwitcherDefaults)
    }
}

/** Valor string de `layer_type` vindo da API (enum do Laravel vira string ou `{ value }`). */
export function mapsLayerTypeValue (layerData) {
    if (!layerData || layerData.layer_type == null) return ''
    const t = layerData.layer_type
    return typeof t === 'string' ? t : String(t.value ?? '')
}

function tableNameFromGeoServerLayerValue (value) {
    if (value == null || value === '') return null
    const s = String(value).trim()
    if (!s) return null
    const idx = s.indexOf(':')
    if (idx === -1) return s
    const rest = s.slice(idx + 1).trim()
    return rest || s
}

export async function createLayerToViewer(layerData) {
    const publicRequest = Boolean(layerData?.__public_request)
    let  style = new Style ({
        fill: new Fill({color: 'green'})
    })
    if (layerData.enable_style_code) {
        let verfiy = verifyLayersCode(layerData.style_code)
        if (verfiy.valid) {
            style = verfiy.layer
        } else {
            style = new Style ({
                fill: new Fill({color: 'green'})
            })
        }
    }

    if (layerData.style) {
        style = layerData.style
    }

    switch (mapsLayerTypeValue(layerData)) {
        case 'VectorLayer': {
            const geojson = await fetchAllFeatures(layerData.geo_table, { publicRequest });

            let layer = new VectorLayer({
                name: layerData.name,
                visible: true,
                source: new VectorSource({
                    features: new GeoJSON().readFeatures(geojson, geoJsonReadOptions()),
                }),
                style: style,
                faq_id: layerData.faq_id,
                displayInLayerSwitcher: layerData.display_in_layer_switcher ?? true,
                actions: layerData?.actions ?? {},
                reference:
                    layerData.action === 'Personalizado' && layerData.attribute_display_html
                        ? layerData.attribute_display_html
                        : layerData?.reference,
                geo_table: layerData?.geo_table
            });

            if (layerData.opacity) {
                layer.setOpacity(layerData.opacity ? layerData.opacity : 1)
            }
            return layer
        }

        case 'ImageLayer': {
            let click = layerData.action ?? null
            let params = layerData.params ? JSON.parse(layerData.params) : {
                LAYERS: layerData.geo_server_layer,
                TILED: true
            };

            return new ImageLayer({
                source: new ImageWMS({
                    url: layerData.external_layer ? layerData.url : geoServerWmsUrl(),
                    params: params,
                    ratio: 1,
                    serverType: 'geoserver'
                }),
                visible: true,
                name: layerData.name,
                faq_id: layerData.faq_id,
                displayInLayerSwitcher: layerData.display_in_layer_switcher ?? true,
                opacity: layerData.opacity ? layerData.opacity : 1,
                geo_server_layer: layerData?.geo_server_layer,
                reference:
                    layerData.action === 'Personalizado' && layerData.attribute_display_html
                        ? layerData.attribute_display_html
                        : null,
                actions: click ? {
                    click: click
                } : null,
            });
        }

        case 'TileLayer': {
            let click = layerData.action ?? null
            let params = layerData.params ? JSON.parse(layerData.params) : {
                LAYERS: layerData.geo_server_layer,
                TILED: true
            };

            return new TileLayer({
                source: new TileWMS({
                    url: layerData.external_layer ? layerData.url : geoServerWmsUrl(),
                    params: params,
                    ratio: 1,
                    serverType: 'geoserver'
                }),
                visible: true,
                name: layerData.name,
                faq_id: layerData.faq_id,
                displayInLayerSwitcher: layerData.display_in_layer_switcher ?? true,
                opacity: layerData.opacity ? layerData.opacity : 1,
                geo_server_layer: layerData?.geo_server_layer,
                reference:
                    layerData.action === 'Personalizado' && layerData.attribute_display_html
                        ? layerData.attribute_display_html
                        : null,
                actions: click ? {
                    click: click
                } : null,
            });
        }

        case 'LayerGroup': {
            const subLayersPromises = [...(layerData.layers || [])].reverse().map((child) =>
                createLayerToViewer({ ...child, __public_request: publicRequest })
            );
            const subLayers = await Promise.all(subLayersPromises);
            const filteredSubLayers = subLayers.filter(Boolean);

            if (filteredSubLayers.length === 0) {
                return null;
            }

            filteredSubLayers.forEach((subLayer) => {
                if (subLayer?.set) {
                    subLayer.set('layerSwitcherGroup', layerData.name || 'Grupo de Camadas');
                }
            })
            
            return new LayerGroup({
                title: layerData.name || 'Grupo de Camadas',
                displayInLayerSwitcher: true,
                visible: true,
                layers: filteredSubLayers,
                actions: layerData?.actions ?? {}
            });
        }

        default:
            console.warn(
                `createLayerToViewer: tipo de camada não suportado (${mapsLayerTypeValue(layerData) || 'indefinido'})`,
                layerData?.name ?? layerData?.geo_table ?? layerData?.geo_server_layer
            )
            return null;
    }
}

export async function createThematicLayer(layerData) {
    const table = `thematic_maps.thematic_${layerData.id}`
    const source = new VectorSource()

    let style = layerData.style
    if (!style) {
        style = new Style({
            fill: new Fill({ color: 'rgba(255, 0, 0, 0.3)' }),
            stroke: new Stroke({ color: '#ff0000', width: 2 })
        })
    }

    const layer = new VectorLayer({
        id: `m-${layerData.id}`,
        layer_id: layerData.id,
        name: layerData.name,
        layer_type: 'VectorLayer',
        visible: layerData.active,
        source: source,
        style: style,
        faq_id: layerData.faq_id,
        displayInLayerSwitcher: layerData.display_in_layer_switcher,
        font: table,
        actions: layerData?.actions ?? {},
        reference: layerData?.reference,
        geo_table: table,
        cartograms: layerData.cartograms
    });

    const loadFeatures = async () => {
        if (layer.get('lazy_loaded') || layer.get('lazy_loading')) return;
        layer.set('lazy_loading', true);
        
        try {
            const geojson = await fetchAllFeatures(table);
            if (!geojson || !geojson.features || !Array.isArray(geojson.features)) {
                console.warn(`Camada ${layerData.name}: GeoJSON inválido ou sem features`, geojson);
                layer.set('lazy_loaded', true);
                return;
            }
            
            const features = new GeoJSON().readFeatures(geojson, geoJsonReadOptions());
            
            if (features && features.length > 0) {
                source.addFeatures(features);
            } else {
                console.warn(`Camada ${layerData.name}: Nenhuma feature foi criada a partir do GeoJSON`);
            }
            
            layer.set('lazy_loaded', true);
        } catch (error) {
            console.error(`Erro ao carregar features da camada ${layerData.name}:`, error);
        } finally {
            layer.set('lazy_loading', false);
        }
    };

    if (layerData.active) {
        await loadFeatures();
    }

    layer.on('change:visible', () => {
        if (layer.getVisible() && !layer.get('lazy_loaded')) {
            loadFeatures();
        }
    });

    return layer;
}

export async function createLayer(layerData) {
    let  style = new Style ({
        fill: new Fill({color: 'green'})
    })

    if (layerData.style) {
        style = layerData.style
    }

    if (layerData.enable_style_code) {
        let verfiy = verifyLayersCode(layerData.style_code)
        if (verfiy.valid) {
            style = verfiy.layer
        } else {
            style = new Style ({
                fill: new Fill({color: 'green'})
            })
        }
    }
    switch (layerData.layer_type) {
        case 'VectorLayer': {
            const source = new VectorSource();

            const clickAction = layerData.action ?? layerData?.actions?.click ?? null

            let layer = new VectorLayer({
                id: layerData.id || null,
                name: layerData.name,
                layer_type: mapsLayerTypeValue(layerData),
                visible: layerData.active,
                source: source,
                style: style,
                faq_id: layerData.faq_id,
                displayInLayerSwitcher: layerData.display_in_layer_switcher,
                font: layerData.geo_table ?? layerData.geo_server_layer,
                action: clickAction,
                actions: {
                    ...(layerData?.actions ?? {}),
                    ...(clickAction ? { click: clickAction } : {}),
                },
                geo_table: layerData?.geo_table,
                reference:
                    layerData.action === 'Personalizado' && layerData.attribute_display_html
                        ? layerData.attribute_display_html
                        : null,
            });

            if (layerData.opacity) layer.setOpacity(layerData.opacity ? layerData.opacity : 1)

            if (layerData.zIndex) layer.setZIndex(Number(layerData.zIndex))

            const loadFeatures = async () => {
                if (layer.get('lazy_loaded') || layer.get('lazy_loading')) return;
                layer.set('lazy_loading', true);
                
                try {
                    const geojson = await fetchAllFeatures(layerData.geo_table);
                    const features = new GeoJSON().readFeatures(geojson, geoJsonReadOptions());
                    source.addFeatures(features);
                    layer.set('lazy_loaded', true);
                } catch (error) {
                    console.error(`Erro ao carregar features da camada ${layerData.name}:`, error);
                } finally {
                    layer.set('lazy_loading', false);
                }
            };

            if (layerData.active) {
                await loadFeatures();
            }

            layer.on('change:visible', () => {
                if (layer.getVisible() && !layer.get('lazy_loaded')) {
                    loadFeatures();
                }
            });

            return layer
        }

        case 'ImageLayer': {
            let click = layerData.action ?? null
            let params = layerData.params ? JSON.parse(layerData.params) : {
                LAYERS: layerData.geo_server_layer,
                TILED: true
            };
            const imageLayer = new ImageLayer({
                source: new ImageWMS({
                    url: layerData.external_layer ? layerData.url : geoServerWmsUrl(),
                    params: params,
                    ratio: 1.5,
                    serverType: 'geoserver'
                }),
                id: layerData.id || null,
                layer_type: mapsLayerTypeValue(layerData),
                order: layerData.order,
                visible: layerData.active,
                is_raster: layerData.is_raster,
                name: layerData.name,
                faq_id: layerData.faq_id,
                displayInLayerSwitcher: layerData.display_in_layer_switcher,
                font: layerData.geo_table ?? layerData.geo_server_layer,
                opacity: layerData.opacity ? layerData.opacity : 1,
                action: click,
                reference:
                    layerData.action === 'Personalizado' && layerData.attribute_display_html
                        ? layerData.attribute_display_html
                        : null,
                actions: click ? {
                    click: click
                } : null,
            });

            if (layerData.zIndex !== undefined) {
                imageLayer.setZIndex(Number(layerData.zIndex))
            }

            return imageLayer
        }

        case 'TileLayer': {
            let click = layerData.action ?? null
            let params = layerData.params ? JSON.parse(layerData.params) : {
                LAYERS: layerData.geo_server_layer,
                STYLES: layerData.geo_server_layer.split(':')[1]+'_geometrias',
                TILED: true
            };

            const applyFallback = () => {
                setTimeout(() => {
                    tileSource.updateParams({STYLES: ''});
                    tileSource.clear();
                    tileSource.refresh();
                }, 0);
            };

            const tileSource = new TileWMS({
                url: layerData.external_layer ? layerData.url : geoServerWmsUrl(),
                params: params,
                ratio: 1.5,
                serverType: 'geoserver',

                tileLoadFunction: (tile, src) => {
                    const image = tile.getImage();

                    image.addEventListener('error', () => {
                        applyFallback();
                    }, { once: true });

                    image.src = src;
                }
            });

            const tileLayer = new TileLayer({
                source: tileSource,
                id: layerData.id || null,
                layer_type: mapsLayerTypeValue(layerData),
                order: layerData.order,
                visible: layerData.display_in_layer_switcher && layerData.active,
                is_raster: layerData.is_raster,
                name: layerData.name,
                faq_id: layerData.faq_id,
                displayInLayerSwitcher: layerData.display_in_layer_switcher,
                font: layerData.geo_table ?? layerData.geo_server_layer,
                opacity: layerData.opacity ? layerData.opacity : 1,
                geo_server_layer: layerData?.geo_server_layer,
                action: click,
                reference:
                    layerData.action === 'Personalizado' && layerData.attribute_display_html
                        ? layerData.attribute_display_html
                        : null,
                actions: click ? {
                    click: click
                } : null,
            });

            if (layerData.zIndex !== undefined) {
                tileLayer.setZIndex(Number(layerData.zIndex))
            }

            const labelParams = {
                LAYERS: layerData.geo_server_layer,
                STYLES: layerData.geo_server_layer.split(':')[1]+'_rotulos',
                FORMAT: 'image/png',
                TRANSPARENT: true,
            };

            let labelLayer;

            const applyLabelFallback = () => {
                labelLayer?.set('ignored', true);
                labelLayer?.setVisible(false);
            };

            const labelSource = new ImageWMS({
                url: layerData.external_layer ? layerData.url : geoServerWmsUrl(),
                params: labelParams,
                ratio: 1.5,
                serverType: 'geoserver',

                imageLoadFunction: (image, src) => {
                    const imageElement = image.getImage();

                    imageElement.addEventListener('error', () => {
                        applyLabelFallback();
                    }, { once: true });

                    imageElement.src = src;
                }
            });

            labelLayer = new ImageLayer({
                source: labelSource,
                id: layerData.id ? `${layerData.id}_labels` : null,
                name: `${layerData.name} - Rótulos`,
                visible: layerData.display_in_layer_switcher && layerData.active,
                opacity: layerData.opacity ?? 1,
                displayInLayerSwitcher: false,
                geo_server_layer: layerData.geo_server_layer,
                is_label_layer: true,
                parent_layer_id: layerData.id ?? null,
            });

            tileLayer.set('labelLayer', labelLayer);
            labelLayer.set('geometryLayer', tileLayer);

            if (layerData.zIndex !== undefined) {
                const geometryZIndex = Number(layerData.zIndex);

                tileLayer.setZIndex(geometryZIndex);
                labelLayer.setZIndex(geometryZIndex + 1);
            }

            return [tileLayer, labelLayer]
        }

        case 'LayerGroup': {
            const subLayersPromises = (layerData.layers?.reverse() || []).map(createLayer);
            const subLayers = await Promise.all(subLayersPromises);
            const filteredSubLayers = subLayers.filter(Boolean);

            if (filteredSubLayers.length === 0) {
                return null;
            }

            filteredSubLayers.forEach((subLayer) => {
                if (subLayer?.set) {
                    subLayer.set('layerSwitcherGroup', layerData.name || 'Grupo de Camadas');
                }
            })
            
            return new LayerGroup({
                title: layerData.name || 'Grupo de Camadas',
                displayInLayerSwitcher: true,
                visible: true,
                layers: filteredSubLayers,
                actions: layerData?.actions ?? {}
            });
        }

        default:
            return null;
    }
}

// =========================================================================
// CREATE EDITABLE LAYER (A versão unificada e dinâmica para WFS + GeoStyler)
// =========================================================================
export async function createEditableLayer(layerData, extent) {
    if (layerData.layer_type === 'LayerGroup') {
        const subLayersPromises = (layerData.layers?.reverse() || []).map(layer => createEditableLayer(layer, extent));
        const subLayers = await Promise.all(subLayersPromises);
        const filteredSubLayers = subLayers.filter(Boolean);

        if (filteredSubLayers.length === 0) return null;

        filteredSubLayers.forEach((subLayer) => {
            if (subLayer?.set) {
                subLayer.set('layerSwitcherGroup', layerData.name || 'Grupo de Camadas');
            }
        })

        return new LayerGroup({
            title: layerData.name || 'Grupo de Camadas',
            displayInLayerSwitcher: true,
            visible: true,
            layers: filteredSubLayers,
            actions: layerData?.actions ?? {}
        });
    }

    const gsLayerName = layerData.geo_server_layer;
    const editableTable = layerData.geo_table || tableNameFromGeoServerLayerValue(gsLayerName);

    if (layerData.is_raster) {
        let click = layerData.action ?? null
        let params = layerData.params ? JSON.parse(layerData.params) : {
            LAYERS: layerData.geo_server_layer,
            TILED: true
        };

        const imageLayer = new ImageLayer({
            source: new ImageWMS({
                url: layerData.external_layer ? layerData.url : geoServerWmsUrl(),
                params: params,
                ratio: 1.5,
                serverType: 'geoserver'
            }),
            id: layerData.id || null,
            layer_type: mapsLayerTypeValue(layerData),
            order: layerData.order,
            visible: layerData.active,
            is_raster: layerData.is_raster,
            name: layerData.name,
            faq_id: layerData.faq_id,
            displayInLayerSwitcher: layerData.display_in_layer_switcher,
            font: layerData.geo_table ?? layerData.geo_server_layer,
            opacity: layerData.opacity ? layerData.opacity : 1,
            action: click,
            reference:
                layerData.action === 'Personalizado' && layerData.attribute_display_html
                    ? layerData.attribute_display_html
                    : null,
            actions: click ? {
                click: click
            } : null,
        });

        if (layerData.zIndex !== undefined) {
            imageLayer.setZIndex(Number(layerData.zIndex))
        }

        return imageLayer
    }

    // ---------------------------------------------------------------------
    // CASO 1: CAMADA DO GEOSERVER (WFS + GeoStyler Automático)
    // ---------------------------------------------------------------------
    if (editableTable) {
        const vectorSource = new VectorSource();

        let vectorLayer = new VectorLayer({
            id: layerData.id || null,
            name: layerData.name,
            originalName: layerData.name,
            source: vectorSource,
            visible: layerData.active !== undefined ? layerData.active : true,
            displayInLayerSwitcher: layerData.display_in_layer_switcher,
            font: layerData.geo_table ?? layerData.geo_server_layer,
            actions: layerData?.actions ?? {},
            geo_table: layerData?.geo_table,
            geo_server_layer: gsLayerName,
        });

        if (layerData.opacity) vectorLayer.setOpacity(layerData.opacity);

        vectorLayer.set('isDirty', false);
        const isMain = layerData.name === 'Lotes' || layerData.name === 'Edificações';
        vectorLayer.set('isMain', isMain);

        const loadFeatures = async () => {
            if (vectorLayer.get('lazy_loaded') || vectorLayer.get('lazy_loading')) return;
            vectorLayer.set('lazy_loading', true);

            try {
                const geojson = await fetchAllFeatures(editableTable);
                const features = new GeoJSON().readFeatures(geojson, {
                    featureProjection: 'EPSG:3857'
                });
                features.forEach((feature) => {
                    const gid = feature.get('gid');
                    if (gid !== undefined && gid !== null && gid !== '') {
                        feature.setId(String(gid));
                    }
                });
                vectorSource.clear();
                vectorSource.addFeatures(features);
                vectorLayer.set('lazy_loaded', true);
                vectorSource.dispatchEvent('featuresloadend');
            } catch (error) {
                console.error(`Erro ao carregar features editaveis da camada ${layerData.name}:`, error);
            } finally {
                vectorLayer.set('lazy_loading', false);
            }
        };

        if (layerData.active) {
            await loadFeatures();
        }

        vectorLayer.on('change:visible', () => {
            if (vectorLayer.getVisible() && !vectorLayer.get('lazy_loaded')) {
                loadFeatures();
            }
        });

        if (gsLayerName) {
            applyGeoServerStyle(vectorLayer, gsLayerName);
        }

        return vectorLayer;
    }

    // ---------------------------------------------------------------------
    // CASO 2: CAMADA DO GEOSERVER (WFS + GeoStyler Automático)
    // ---------------------------------------------------------------------
    if (gsLayerName) {

// Extrai o workspace dinamicamente
        const workspace = gsLayerName.includes(':') ? gsLayerName.split(':')[0] : '';

        const vectorSource = new VectorSource({
            format: new GeoJSON(),
            url: function (extentParams) {
                const baseUrl = window.geoServerUrl || 'http://172.19.192.9:8080';

                // Rota OWS (WFS) idêntica à que funcionava na sua API
                const wfsPath = workspace ? `geoserver/${workspace}/ows` : `geoserver/wfs`;

                const wfsUrl = `${baseUrl}/${wfsPath}?service=WFS&version=1.0.0&request=GetFeature&typeName=${gsLayerName}&outputFormat=application/json&srsname=EPSG:3857&bbox=${extentParams.join(',')},EPSG:3857`;

                return proxyGeoServerUrl(wfsUrl);
            },
            strategy: bboxStrategy,
        });

        // Usamos VectorLayer raiz, que é o melhor formato para o MapEditor
        let vectorLayer = new VectorLayer({
            id: layerData.id || null,
            name: layerData.name,
            originalName: layerData.name,
            source: vectorSource,
            visible: layerData.active !== undefined ? layerData.active : true,
            displayInLayerSwitcher: layerData.display_in_layer_switcher,
            font: layerData.geo_table ?? layerData.geo_server_layer,
            actions: layerData?.actions ?? {},
            geo_table: layerData?.geo_table,
            geo_server_layer: gsLayerName,
        });

        if (layerData.opacity) vectorLayer.setOpacity(layerData.opacity);

        vectorLayer.set('isDirty', false);
        const isMain = layerData.name === 'Lotes' || layerData.name === 'Edificações';
        vectorLayer.set('isMain', isMain);

        // A estratégia de BBOX já gerencia o loading perfeitamente.
        vectorLayer.set('lazy_loaded', true);

        // Aplica as cores do SLD (GeoStyler) em background!
        applyGeoServerStyle(vectorLayer, gsLayerName);

        return vectorLayer;
    }

    // ---------------------------------------------------------------------
    // CASO 2: CAMADA LOCAL (Sem GeoServer, via DB Local) -> MANTÉM COMPATIBILIDADE
    // ---------------------------------------------------------------------
    const getDynamicStyle = (type) => {
        if (type === 'Polygon' || type === 'MultiPolygon') {
            return new Style({
                fill: new Fill({color: 'rgba(51, 153, 204, 0.4)'}),
                stroke: new Stroke({ color: '#3399CC', width: 2 })
            });
        }
        if (type === 'LineString' || type === 'MultiLineString') {
            return new Style({ stroke: new Stroke({ color: '#ff6a00', width: 3 }) });
        }
        if (type === 'Point' || type === 'MultiPoint') {
            return new Style({
                image: new CircleStyle({ radius: 6, fill: new Fill({color: '#3399CC'}), stroke: new Stroke({ color: '#ffffff', width: 2 }) })
            });
        }
        return null;
    };

    const source = new VectorSource();

    let layer = new VectorImageLayer({
        id: layerData.id || null,
        name: layerData.name,
        originalName: layerData.name,
        visible: layerData.active !== undefined ? layerData.active : true,
        source: source,
        faq_id: layerData.faq_id,
        style: (feature) => getDynamicStyle(feature.getGeometry().getType()),
        displayInLayerSwitcher: layerData.display_in_layer_switcher,
        font: layerData.geo_table ?? layerData.geo_server_layer,
        actions: layerData?.actions ?? {},
        geo_table: layerData?.geo_table,
    });

    if (layerData.opacity) layer.setOpacity(layerData.opacity);
    layer.set('isDirty', false);
    const isMainLocal = layerData.name === 'Lotes' || layerData.name === 'Edificações';
    layer.set('isMain', isMainLocal);

    const loadFeatures = async () => {
        if (layer.get('lazy_loaded') || layer.get('lazy_loading')) return;
        layer.set('lazy_loading', true);

        try {
            const geojson = await fetchAllFeatures(layerData.geo_table);
            const features = new GeoJSON().readFeatures(geojson, geoJsonReadOptions());
            source.addFeatures(features);
            layer.set('lazy_loaded', true);
        } catch (error) {
            console.error(`Erro ao carregar features locais da camada ${layerData.name}:`, error);
        } finally {
            layer.set('lazy_loading', false);
        }
    };

    if (layerData.active) {
        await loadFeatures();
    }

    layer.on('change:visible', () => {
        if (layer.getVisible() && !layer.get('lazy_loaded')) {
            loadFeatures();
        }
    });

    return layer;
}

export function findLayerWithFeature(map, feature) {
    function search(layers) {
        for (const layer of layers) {
            if (layer.getLayers) {
                const found = search(layer.getLayers().getArray())
                if (found) return found
            } else if (layer.getSource?.()?.hasFeature?.(feature)) {
                return layer
            }
        }
        return null
    }
    return search(map.getLayers().getArray())
}

export function getMapFeaturesByLayer(layerName, map) {
    let mapLayer = map.getLayers().getArray().find(layer => {
        return layer.get('name') === layerName
    })

    if (!mapLayer) {
        mapLayer = map.getLayers().getArray().find(layer => {
            return layer.get('layerName') === layerName
        })
    }

    if (mapLayer) {
        const source = mapLayer.getSource()
        return source.getFeatures()
    }
    return null
}

export function verifyLayersCode(code) {
    let layerInstance = null;

    try {
        const runCode = new Function(
            'VectorLayer', 'ImageLayer', 'TileLayer', 'LayerGroup',
            'VectorSource', 'ImageWMS', 'TileWMS', 'OSM',
            'Style', 'Fill', 'Stroke', 'Text', 'CircleStyle', 'Icon',
            'GeoJSON', 'console',
            `
            return ${code};
        `
        );

        layerInstance = runCode(
            VectorLayer, ImageLayer, TileLayer, LayerGroup,
            VectorSource, ImageWMS, TileWMS, OSM,
            Style, Fill, Stroke, Text, CircleStyle, Icon,
            GeoJSON, console
        );

        if (
            layerInstance instanceof VectorLayer ||
            layerInstance instanceof ImageLayer ||
            layerInstance instanceof TileLayer ||
            layerInstance instanceof LayerGroup ||
            layerInstance instanceof Style ||
            typeof layerInstance === 'function'

        ) {
            return {valid: true, layer: layerInstance}
        } else {
            console.warn('Código executado mas não retornou uma camada válida:', layerInstance);
            return {valid: false, layer: null}
        }

    } catch (error) {
        console.error('Erro ao executar o código da camada:', error);
        return {valid: false, layer: null}
    }
}

export function addLocationIconToCoordinate(map, coords) {
    removeLayerByName(map, 'location-icon')

    const layersZIndexInfo = map.getLayers().getArray().map((layer) => ({
        name: layer.get('name') || layer.get('title') || layer.get('layerName') || 'Camada sem nome',
        visible: layer?.getVisible ? layer.getVisible() : false,
        zIndex: Number(layer.getZIndex?.() ?? 0) || 0
    }))

    console.table(layersZIndexInfo)

    const highestLayerZIndex = layersZIndexInfo.reduce((highest, layer) => {
        if (!layer.visible) {
            return highest;
        }

        return Math.max(highest, layer.zIndex);
    }, 0);

    const pointFeature = new Feature({
        geometry: new Point(coords),
    });

    pointFeature.setStyle(
        new Style({
            image: new CircleStyle({
                radius: 8,
                fill: new Fill({ color: '#1E90FF' }),
                stroke: new Stroke({ color: '#ffffff', width: 2 }),
            }),
        })
    );

    const vectorLayer = new VectorLayer({
        source: new VectorSource({
            features: [pointFeature],
        }),
        visible: true,
        zIndex: highestLayerZIndex + 1,
        displayInLayerSwitcher: false
    });

    vectorLayer.set('name', 'location-icon');
    map.addLayer(vectorLayer);
}

export function removeLayerByName(map, name) {
    map.getLayers().forEach((layer) => {
        if (layer.get('name') === name) {
            map.removeLayer(layer);
        }
    });
}

export function removeSelectLayers(map) {
    const layers = map.getLayers().getArray().slice();
    layers.forEach(layer => {
        let name = layer.get('name');
        if (typeof name === 'string' && name.startsWith('select-')) {
            map.removeLayer(layer);
        }

        name = layer.get('layerName');
        if (typeof name === 'string' && name.startsWith('select-')) {
            map.removeLayer(layer);
        }
    });
}

export function getLayerByName(map, name) {
    let ly = map.getLayers().getArray().find((layer) => layer.get('name') === name);
    if (!ly) {
        ly = map.getLayers().getArray().find((layer) => layer.get('layerName') === name);
    }
    return ly;
}

export async function checkAreaOfInterest() {
    if (!window.checkLocation) {
        return {
            success: true,
            message: '',
            coords: getDefaultMapCenter()
        }
    }

    const features = await getFeaturesCityToIBGE('4308805');

    if (!("geolocation" in navigator)) {
        return {
            success: false,
            message: 'Seu navegador não suporta a API de localização.',
            coords: null
        }
    }

    try {
        const deviceLocation = await getDeviceLocation()

        const transformedCoord = fromLonLat([
            deviceLocation.longitude,
            deviceLocation.latitude
        ])

        const isInside = features.some((feature) => {
            const geometry = feature.getGeometry()
            return geometry.intersectsCoordinate(transformedCoord)
        })

        if (isInside) {
            return {
                success: true,
                message: 'Você está dentro da área do município!',
                coords: transformedCoord
            }
        } else {
            return {
                success: false,
                message: 'Você está FORA da área do município!',
                coords: transformedCoord
            }
        }
    } catch (e) {
        return {
            success: false,
            message: 'Erro ao obter sua localização.',
            coords: null
        }
    }
}

export function getDeviceLocation() {
    return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                })
            },
            (error) => {
                console.error('Erro ao obter localização:', error)
                reject(error)
            },
            {
                enableHighAccuracy: false,
                timeout: 20000,
                maximumAge: 0
            }
        )
    })
}

export async function getFeaturesCityToIBGE(idIBGE) {
    try {
        const { data } = await window.axios.get(`proxy/ibge/malha/${idIBGE}`)

        const geojsonFormat = new GeoJSON()

        return geojsonFormat.readFeatures(data, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857'
        });
    } catch (error) {
        console.error('Erro ao buscar polígono:', error)
        return null
    }
}

export function getAllLayersFromMap(map) {
    const allLayers = getAllLayersRecursive(map);
    return allLayers
        .filter(layer => layer.getVisible())
        .sort((a, b) => (b.getZIndex() || 0) - (a.getZIndex() || 0)).reverse();
}

export function getAllLayersRecursive(groupOrMap) {
    const result = [];

    const layers = groupOrMap.getLayers().getArray();
    layers.forEach(layer => {
        if (layer instanceof LayerGroup) {
            if (layer.getVisible()) {
                result.push(...getAllLayersRecursive(layer));
            }
        } else {
            let name = layer.get('name')
            if (name && !name.startsWith('select-')) {
                result.push(layer);
            }
        }
    });

    return result;
}

export function getFirstActiveLayer(map) {
    let layers = getAllLayersFromMap(map)
    return layers[0]
}

/** Camada ImageLayer ou TileLayer cadastrada (ignora tiles de base do mapa). */
export function isRasterMapsLayer(layer) {
    if (!layer) return false
    const layerType = layer.get?.('layer_type')
    if (layerType === 'ImageLayer' || layerType === 'TileLayer') return true
    const layerId = layer.get?.('id')
    if (layerId != null && layerId !== '' && (layer instanceof ImageLayer || layer instanceof TileLayer)) {
        return true
    }
    return false
}

/** Pixel sobre conteúdo visível da camada raster (alpha > 0 quando getData disponível). */
export function isPixelOnRasterLayer(layer, pixel) {
    if (typeof layer.getData !== 'function') {
        return true
    }
    try {
        const data = layer.getData(pixel)
        if (!data) return false
        const alpha = data.length >= 4 ? data[3] : 255
        return alpha !== 0
    } catch {
        return true
    }
}

/**
 * Camada raster visível no pixel, do topo para a base (maior z-index primeiro).
 */
export function findRasterLayerAtPixel(map, pixel, predicate = () => true) {
    for (const layer of getVisibleLayersTopFirst(map)) {
        if (!isRasterMapsLayer(layer)) continue
        if (!predicate(layer)) continue
        if (!isPixelOnRasterLayer(layer, pixel)) continue
        return layer
    }
    return null
}

function layerHasMapClickAction(layer) {
    if (!layer) return false
    const action = getLayerClickAction(layer)
    return action === 'BIC' || isPersonalizadoLayer(layer)
}

/**
 * Primeira camada ativa do layer switcher (maior z-index) com ação BIC ou Personalizado.
 */
export function findPrimaryClickLayer(map) {
    let bestLayer = null
    let bestZ = -Infinity

    function visit(container, inheritedZ = 0) {
        const layers = container.getLayers?.().getArray?.() ?? []
        for (const layer of layers) {
            if (!layer.getVisible()) continue
            const z = Math.max(layer.getZIndex?.() ?? 0, inheritedZ)

            if (layer instanceof LayerGroup) {
                if (layerHasMapClickAction(layer) && z > bestZ) {
                    bestZ = z
                    bestLayer = layer
                }
                visit(layer, z)
                continue
            }

            const name = layer.get('name')
            if (name?.startsWith('select-')) continue
            if (layerHasMapClickAction(layer) && z > bestZ) {
                bestZ = z
                bestLayer = layer
            }
        }
    }

    visit(map)
    return bestLayer
}

/** Primeira camada visível com ação BIC (busca por matrícula / menu). */
export function findLayerWithClickAction(map) {
    for (const layer of getVisibleLayersTopFirst(map)) {
        if (getLayerClickAction(layer) === 'BIC') {
            return layer
        }
    }
    return null
}

export function substituteAttributeTemplate(template, props) {
    if (!template) return ''
    const p = props && typeof props === 'object' ? props : {}
    return String(template).replace(/\$([a-zA-Z0-9_]+)/g, (_, key) => (p[key] != null ? String(p[key]) : ''))
}

export function getLayerClickAction(layer) {
    if (!layer) return null
    return layer.get('actions')?.click ?? layer.get('action') ?? null
}

export function isPersonalizadoLayer(layer) {
    if (!layer) return false
    const action = getLayerClickAction(layer)
    const template = layer.get('reference')
    return action === 'Personalizado' && template && String(template).trim().length > 0
}

/** Camadas visíveis do topo para a base (maior z-index primeiro). */
export function getVisibleLayersTopFirst(map) {
    return getAllLayersRecursive(map)
        .filter((layer) => layer.getVisible())
        .sort((a, b) => (b.getZIndex() || 0) - (a.getZIndex() || 0))
}

/**
 * Camada raster Personalizado do clique: só a camada primária do switcher, se for raster.
 */
export function findPersonalizadoRasterLayerAtPixel(map, pixel) {
    const primaryLayer = findPrimaryClickLayer(map)
    if (!primaryLayer || !isPersonalizadoLayer(primaryLayer) || !isRasterMapsLayer(primaryLayer)) {
        return null
    }
    if (!isPixelOnRasterLayer(primaryLayer, pixel)) {
        return null
    }
    return primaryLayer
}

/** Camadas vetoriais Personalizado (inclui filhas de LayerGroup). */
export function collectPersonalizadoVectorLayers(layer, target = []) {
    if (layer instanceof LayerGroup) {
        layer.getLayers().getArray().forEach((child) => collectPersonalizadoVectorLayers(child, target))
        return target
    }
    if (layer.get?.('layer_type') === 'VectorLayer' && isPersonalizadoLayer(layer)) {
        target.push(layer)
    }
    return target
}

function hexToRgba(hex, alpha = 1) {
    if (!hex) return `rgba(0,0,0,${alpha})`
    const h = hex.replace('#', '')
    const bigint = parseInt(h, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return `rgba(${r},${g},${b},${alpha})`
}

export function buildStyleFunctionFromBackend(layerMeta) {
    let type = layerMeta?.geometry_type
    if (type && typeof type === 'object' && type.value) {
        type = type.value
    }
    if (typeof type !== 'string') {
        type = String(type || '')
    }
    
    const fillColor   = layerMeta?.fill_color   || '#ffffff'
    const strokeColor = layerMeta?.border_color || '#000000'
    const strokeWidth = Number(layerMeta?.border_size ?? 1)
    const iconPath    = layerMeta?.icon_path || null
    const iconScale   = Number(layerMeta?.icon_scale ?? 0.15)

    if (type === 'Point' || type === 'MultiPoint') {
        if (!iconPath) {
            const pointStyle = new Style({
                image: new CircleStyle({
                    radius: 5,
                    fill: new Fill({ color: fillColor }),
                    stroke: new Stroke({ color: strokeColor, width: strokeWidth })
                })
            })
            return function pointStyleFn() { return pointStyle }
        }
        
        const pointIcon = new Icon({
            src: iconPath,
            anchor: [0.5, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            scale: iconScale || 0.15
        })

        const pointStyle = new Style({ image: pointIcon })

        return function pointStyleFn() { return pointStyle }
    }

    if (type === 'LineString' || type === 'MultiLineString') {
        const lineStyle = new Style({
            stroke: new Stroke({ color: strokeColor, width: strokeWidth })
        })
        return function lineStyleFn() { return lineStyle }
    }

    const polyStyle = new Style({
        fill: new Fill({ color: hexToRgba(fillColor, 1) }),
        stroke: new Stroke({ color: strokeColor, width: strokeWidth })
    })

    let interiorIconStyle = null
    if (iconPath) {
        interiorIconStyle = new Style({
            image: new Icon({
                src: iconPath,
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                scale: iconScale || 1
            }),
            geometry: (feature) => {
                const geom = feature.getGeometry()
                return geom && geom.getInteriorPoint ? geom.getInteriorPoint() : null
            }
        })
    }

    return function polygonStyleFn() {
        return interiorIconStyle ? [polyStyle, interiorIconStyle] : polyStyle
    }
}

export const getAttributeOptionLabel = (attribute) => {
    const parts = String(attribute ?? '')
        .split('.')
        .filter(Boolean);

    return parts[parts.length - 1] || String(attribute ?? '');
};

const getFeatureAttributeValue = (feature, attribute) => {
    if (!feature || !attribute) return undefined;

    const directValue = feature.get(attribute);
    if (directValue !== undefined) return directValue;

    const fallbackKey = getAttributeOptionLabel(attribute);
    if (!fallbackKey || fallbackKey === attribute) return undefined;

    return feature.get(fallbackKey);
};

const createLabelText = (feature, labelField, labelColor, labelSize, labelFont) => {
    const dynamicLabelField = feature.get('customLabelField') || labelField;

    if (!dynamicLabelField || dynamicLabelField === 'NONE') return null;

    const value = getFeatureAttributeValue(feature, dynamicLabelField);
    if (value === undefined || value === null || value === '') return null;
    const geometryType = feature.getGeometry?.()?.getType?.();

    return new Text({
        text: String(value),
        font: `bold ${labelSize}px ${labelFont}`,
        fill: new Fill({ color: labelColor }),
        stroke: new Stroke({ color: '#fff', width: 3 }),
        offsetY: geometryType === 'Point' ? 25 : 0,
        overflow: true
    });
};

const createFeatureIconStyle = (feature, resolution = 1) => {
    const useIcon = feature.get('useIcon');
    const iconPath = feature.get('iconPath');

    if (!useIcon || !iconPath) return null;

    const geometry = feature.getGeometry();
    if (!geometry) return null;

    const pointGeometry = geometry.getType?.() === 'Point'
        ? geometry
        : typeof geometry.getInteriorPoint === 'function'
            ? geometry.getInteriorPoint()
            : new Point(getCenter(geometry.getExtent()));
    const featureStroke = feature.get('strokeColor') || '#cc9900';

    return new Style({
        geometry: pointGeometry,
        image: new Photo({
            src: iconPath,
            radius: 18,
            crop: true,
            kind: 'anchored',
            shadow: 2,
            stroke: new Stroke({ color: featureStroke, width: 2 }),
        })
    });
};

const normalizeStyles = (styleValue) => {
    if (!styleValue) return [];
    return Array.isArray(styleValue) ? styleValue.filter(Boolean) : [styleValue];
};

const stripTextFromStyle = (style) => {
    if (!style) return null;
    if (typeof style.clone !== 'function') return style;

    const clonedStyle = style.clone();
    clonedStyle.setText(undefined);
    return clonedStyle;
};

export const getTableReference = (layer) => layer?.get('geo_table') ?? layer?.get('font') ?? layer?.get('geo_server_layer')